import {
    Button,
    CustomTable,
    OptionType,
    Pagination,
    TableType,
    PhytoViewType,
} from "modules/common";
import { useMemo, useState } from "react";
import { SortingRule } from "react-table";
import { PhytoviewEntry, PhytoViewEntryResponse } from "services/phytoview";
import PhytoViewSelectModal from "./PhytoViewSelectModal";
import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";

export interface IPhytoViewTable {
    phytoviews?: PhytoViewEntryResponse;
    isLoading?: boolean;
    setSortBy?: (sortBy: SortingRule<object>) => void;
    page?: number;
    pageSize?: number;
    onPageSizeChange?: (value: number) => void;
    onPageChange?: (page: number) => void;
    className?: string;
    phytoviewType: PhytoViewType;
    selectedOption: PhytoViewType;
}

const defaultTableColumns = (phytoviewType: PhytoViewType) => {
    const columnArray = [
        {
            Header: "ID",
            accessor: "id",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.id}</span>;
            },
        },
        {
            Header: "NAZIV",
            accessor: "name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.name}</span>;
            },
        },
        {
            Header: "OPIS",
            accessor: "description",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.description}</span>;
            },
        },
        {
            Header: "AKTIVAN",
            accessor: "active",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.active ? "Da" : "Ne"}</span>;
            },
        },
    ];
    if (
        phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentCultures ||
        phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentSorts ||
        phytoviewType === PhytoViewType.HarmfulOrganismsSpecies ||
        phytoviewType === PhytoViewType.HarmfulOrganismsType ||
        phytoviewType === PhytoViewType.HarmfulOrganismsCultures
    ) {
        columnArray.push({
            Header: "LATINSKI NAZIV",
            accessor: "nameLat",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.nameLat ?? ""}</span>;
            },
        });
    }
    if (phytoviewType === PhytoViewType.JLS) {
        columnArray.push({
            Header: "ŽUPANIJA",
            accessor: "vrstaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.vrstaId?.name ?? ""}</span>;
            },
        });
    }
    if (
        phytoviewType === PhytoViewType.HarmfulOrganismsSubCategories ||
        phytoviewType === PhytoViewType.HarmfulOrganismsSpecies ||
        phytoviewType === PhytoViewType.HarmfulOrganismsType ||
        phytoviewType === PhytoViewType.CultivationArea ||
        phytoviewType === PhytoViewType.HarmfulOrganismsAttributesOfTheTypepOfHarmfulOrganism ||
        phytoviewType ===
            PhytoViewType.HarmfulOrganismsAttributesOfASubsetOfTheTypeOfHarmfulOrganism ||
        phytoviewType === PhytoViewType.HarmfulOrganismsCultures
    ) {
        columnArray.push({
            Header: "KATEGORIJA",
            accessor: "kategorija.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.kategorija?.name ?? ""}</span>;
            },
        });
    }
    if (
        phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentSubCategories ||
        phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentCultures ||
        phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentSorts
    ) {
        columnArray.push({
            Header: "KATEGORIJA",
            accessor: "kategorija.name",
            width: 3,
            Cell: () => {
                return <span className="table-name">{"Poljoprivreda"}</span>;
            },
        });
    }
    if (
        phytoviewType === PhytoViewType.HarmfulOrganismsCultures ||
        phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentCultures
    ) {
        columnArray.push({
            Header: "POTKATEGORIJA",
            accessor: "vrstaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.vrstaId?.name ?? ""}</span>;
            },
        });
    }
    if (phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentSorts) {
        columnArray.push({
            Header: "KULTURA",
            accessor: "vrstaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.vrstaId?.name ?? ""}</span>;
            },
        });
    }
    if (phytoviewType === PhytoViewType.HarmfulOrganismsType) {
        columnArray.push({
            Header: "ŠTETNI ORGANIZAM",
            accessor: "vrstaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.vrstaId?.name ?? ""}</span>;
            },
        });
    }
    if (phytoviewType === PhytoViewType.CultivationArea) {
        columnArray.push({
            Header: "ATRIBUT OPAŽANJA",
            accessor: "vrstaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.vrstaId?.name ?? ""}</span>;
            },
        });
    }
    if (
        phytoviewType ===
        PhytoViewType.HarmfulOrganismsAttributesOfASubsetOfTheTypeOfHarmfulOrganism
    ) {
        columnArray.push({
            Header: "ŠTETNI ORGANIZAM ATRIBUT",
            accessor: "vrstaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.vrstaId?.name ?? ""}</span>;
            },
        });
    }
    if (phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentTheMainPhases) {
        columnArray.push({
            Header: "KULTURA",
            accessor: "predmetOpazanjaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.predmetOpazanjaId?.name ?? ""}</span>;
            },
        });
    }
    if (phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentSubphases) {
        columnArray.push({
            Header: "GLAVNA FAZA",
            accessor: "vrstaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return <span className="table-name">{original.vrstaId?.name ?? ""}</span>;
            },
        });
    }
    if (phytoviewType === PhytoViewType.PhasesOfPlantGrowthAndDevelopmentSubphases) {
        columnArray.push({
            Header: "KULTURA",
            accessor: "vrstaId.predmetOpazanjaId.name",
            width: 3,
            Cell: ({ row }: any) => {
                const { original } = row;
                return (
                    <span className="table-name">
                        {original.vrstaId?.predmetOpazanjaId?.name ?? ""}
                    </span>
                );
            },
        });
    }
    return columnArray;
};

export const PhytoViewTable = ({
    phytoviews,
    isLoading,
    setSortBy,
    page,
    pageSize,
    onPageSizeChange,
    onPageChange,
    className,
    phytoviewType,
    selectedOption,
}: IPhytoViewTable) => {
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState<PhytoviewEntry>();
    const [isPost, setIsPost] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editItemId, setEditItemId] = useState<number | null>(null);

    const tableColumns = useMemo(() => {
        let columns = defaultTableColumns(phytoviewType);
        if (!setSortBy) {
            columns = columns.map(column => ({ ...column, disableSortBy: true }));
        }
        const actionColumn = {
            Header: "",
            accessor: "actions",
            id: "info",
            disableSortBy: true,
            width: 2,
            className: "actions-cell",
            Cell: ({ row }: any) => {
                return (
                    <>
                        <Button
                            type="button"
                            size="small"
                            variant="primary-ghost"
                            disabled={false}
                            onClick={() => {
                                setModalOpen(true);
                                setSelectedRow(row.original);
                                handlePutMethod(row.original.id);
                            }}
                        >
                            <span>Uredi</span>
                        </Button>
                    </>
                );
            },
        };
        columns = [...columns, actionColumn];
        return columns;
    }, [phytoviewType, setSortBy]);

    const activePageSize = useMemo(() => {
        return pageSize
            ? { value: pageSize.toString(), label: pageSize.toString() }
            : DEFAULT_PAGE_SIZE_OPTION;
    }, [pageSize]);

    const classNameString = useMemo(() => {
        return `phytoview-table ${className ?? ""}`;
    }, [className]);

    const handlePageSizeChange = (option: OptionType | null) => {
        onPageSizeChange &&
            onPageSizeChange(parseInt(option?.value ?? DEFAULT_PAGE_SIZE_OPTION.value));
    };
    const handlePageClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        const target = event.currentTarget ?? event.target;
        onPageChange && onPageChange(Number(parseInt(target.id)));
    };
    const handlePageSelect = (page: number) => {
        onPageChange && onPageChange(page);
    };
    const handleNexPageClick = () => {
        onPageChange && onPageChange(page ? page + 1 : 1);
    };

    const handlePrevPageClick = () => {
        onPageChange && onPageChange(page ? page - 1 : 1);
    };
    const handleFirstPageClick = () => {
        onPageChange && onPageChange(1);
    };
    const handleLastPageClick = () => {
        if (phytoviews?.items && phytoviews.items.length > 0)
            onPageChange &&
                onPageChange(Math.ceil(phytoviews.total / parseInt(activePageSize.value)));
    };
    const handlePutMethod = (id: number) => {
        setIsPost(false);
        setEditItemId(id);
        setIsModalOpen(true);
    };
    const handleClose = () => {
        setIsModalOpen(false);
        setEditItemId(null);
    };
    return (
        <div className={classNameString}>
            <CustomTable
                flex
                data={phytoviews?.items ?? []}
                columns={tableColumns}
                loading={isLoading}
                setSortBy={setSortBy}
                tableType={TableType.AdministrationTable}
                disableSort={true}
            />
            {onPageChange && phytoviews?.items && phytoviews.items.length > 0 && (
                <Pagination
                    className="mt-24"
                    currentPage={page ?? 1}
                    numberOfPages={Math.ceil(phytoviews.total / parseInt(activePageSize.value))}
                    pageNeighbours={1}
                    pageSize={activePageSize}
                    onPageSizeChange={handlePageSizeChange}
                    handlePageClick={handlePageClick}
                    handlePageSelect={handlePageSelect}
                    next={handleNexPageClick}
                    prev={handlePrevPageClick}
                    first={handleFirstPageClick}
                    last={handleLastPageClick}
                />
            )}
            {isModalOpen && (
                <PhytoViewSelectModal
                    selectedOption={selectedOption}
                    isPost={isPost}
                    isModalOpen={modalOpen}
                    editItemId={editItemId}
                    onClose={handleClose}
                />
            )}
        </div>
    );
};
